import { ComponentProps, useEffect, useState } from 'react';
import type { Dayjs } from 'dayjs';
import DeleteOutlined from '@ant-design/icons/lib/icons/DeleteOutlined';
import DownloadOutlined from '@ant-design/icons/lib/icons/DownloadOutlined';
import UploadOutlined from '@ant-design/icons/lib/icons/UploadOutlined';
import Papa from 'papaparse';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { opTime } from 'utils/dates';
import { OpUpload } from 'new-components/DLS/OpUpload/OpUpload';
import { OpTypography } from 'new-components/DLS/OpTypography/OpTypography';
import { tableStateKeys } from 'new-components/DLS/OpTableCore/constants/tableStateKeys';
import { OpTableRawColumnType } from 'new-components/DLS/OpTableCore/types';
import { OpTable } from 'new-components/DLS/OpTable/OpTable';
import { OpCollapse } from 'new-components/DLS/OpCollapse/OpCollapse';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpPage } from 'new-components/OpPage/OpPage';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { useEndpointPermissions } from 'utils/customHooks/useEndpointPermissions';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive, heliumSend } from 'utils/helpers';

import { DATE_TIME_FORMAT } from '../../constants/dates';
import { toUTCByTimezone } from '../../utils/dates';
import STATUS from '../../constants/status';
import {
  fetchInvitationConfig,
  VisitorInvitationState,
} from '../../store/slices/visitorInvitationSlice';
import {
  fetchLocations,
  LocationsState,
} from '../../store/slices/locationsSlice';
import {
  fetchVisits,
  setVisitsEndDate,
  setVisitsStartDate,
} from '../../store/slices/visitsSlice';
import { AppDispatch, ImmutableRootState } from '../../store/store';
import {
  ConfigItem,
  InvitationConfig,
} from '../../types/visitorInvitationTypes';
import { User } from '../../types/userTypes';
import { VisitorType } from '../../types/VisitorTypeTypes';
import { formatFullName } from '../../utils/utils';

const SAMPLE_CSV_CONTENT = `Location,First Name,Last Name,Email,Visit Phone,Visitor Type,Start Time,End Time,Visitor Company,Host,Visit Purpose,Visit Note,Group First Name,Group Last Name
Enterprise Marketing Solutions,John,Doe,John.Doe@test.com,+1555551234,Visitor,2025-08-22 9:00 AM,2025-08-22 10:30 AM,Candy Corp,Anne Smith,Contract proposal review,Needs meeting room with projector access,,
,Alice,Smith,alice.smith@test.com,+1555551235,,,,,,,,John,Doe`;

const { Title } = OpTypography;

type UploadList = NonNullable<ComponentProps<typeof OpUpload>['fileList']>;

type BulkScheduleCsvRow = Record<string, string>;

interface ExcludedHost {
  userId: number;
}

const BulkSchedule = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();

  // Check permissions for bulk visitor operations
  const { isValid: hasVisitorsBulkWrite } = useValidateScopes([
    'o{orgId}-visitorVisitorsBulk:w',
  ]);

  const invitationConfig = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationConfig']),
  ) as VisitorInvitationState['invitationConfig'];
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];

  const [fileList, setFileList] = useState<UploadList>([]);
  const [uploadStatus, setUploadStatus] = useState<JSX.Element[] | null>(null);
  const [validEntries, setValidEntries] = useState<BulkScheduleCsvRow[]>([]);

  const [visitorTypes, setVisitorTypes] = useState<VisitorType[]>([]);
  const [hosts, setHosts] = useState<User[]>([]);
  const [sampleCsvUrl, setSampleCsvUrl] = useState<string>('');

  const confirmModal = useConfirmModal();

  // Create blob URL for sample CSV
  useEffect(() => {
    const blob = new Blob([SAMPLE_CSV_CONTENT], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    setSampleCsvUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, []);

  // Check if user has permission to fetch invitation config
  // (prevents 403 error: "You do not have the required permission to method that get a invitation config")
  const { hasReadPermission: hasInvitationConfigRead } = useEndpointPermissions(
    'getInvitationSettings',
  );
  const { hasReadPermission: hasUsersRead } =
    useEndpointPermissions('listUsers');
  const { hasReadPermission: hasExcludeHostsRead } = useEndpointPermissions(
    'listAllExcludeHosts',
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!orgId) return;

      try {
        // Only fetch invitation config if user has the required scopes
        if (!invitationConfig && hasInvitationConfigRead) {
          dispatch(fetchInvitationConfig({ orgId }));
        }
        if (locations.length === 0) {
          dispatch(fetchLocations({ orgId, status: 1 }));
        }

        let usersData: User[] = [];
        let excludedHostsData: ExcludedHost[] = [];

        if (hasUsersRead && hasExcludeHostsRead) {
          const [usersResponse, excludedHostsResponse] = await Promise.all([
            heliumReceive('listUsers', [orgId]),
            heliumReceive('listAllExcludeHosts', [orgId]),
          ]);
          usersData = Array.isArray(usersResponse?.data)
            ? (usersResponse.data as User[])
            : [];
          excludedHostsData = Array.isArray(excludedHostsResponse?.data)
            ? (excludedHostsResponse.data as ExcludedHost[])
            : [];
        } else {
          if (hasUsersRead) {
            const usersResponse = await heliumReceive('listUsers', [orgId]);
            usersData = Array.isArray(usersResponse?.data)
              ? (usersResponse.data as User[])
              : [];
          }
          if (hasExcludeHostsRead) {
            const excludedHostsResponse = await heliumReceive(
              'listAllExcludeHosts',
              [orgId],
            );
            excludedHostsData = Array.isArray(excludedHostsResponse?.data)
              ? (excludedHostsResponse.data as ExcludedHost[])
              : [];
          }
        }

        const activeUsers = usersData.filter(
          (user) => String(user.status) === 'A',
        );
        const excludedIds = excludedHostsData.map(
          (excludedHost) => excludedHost.userId,
        );

        const filteredHosts = activeUsers.filter(
          (host) => !excludedIds.includes(host.id),
        );
        setHosts(filteredHosts);

        const visitorTypesResponse = await heliumReceive('getVisitorTypes', [
          orgId,
        ]);
        setVisitorTypes(
          Array.isArray(visitorTypesResponse?.data)
            ? (visitorTypesResponse.data as VisitorType[])
            : [],
        );
      } catch {
        // Failed to fetch initial data
      }
    };
    fetchData();
  }, [
    dispatch,
    orgId,
    invitationConfig,
    locations.length,
    hasInvitationConfigRead,
    hasUsersRead,
    hasExcludeHostsRead,
  ]);

  const columns: OpTableRawColumnType<{
    field: string;
    required: string;
    acceptable: string;
    description: string;
  }>[] = [
    {
      label: t('bulkSchedule.fields.fieldName'),
      dataIndex: 'field',
      key: 'field',
    },
    {
      label: t('bulkSchedule.fields.required'),
      dataIndex: 'required',
      key: 'required',
    },
    {
      label: t('bulkSchedule.fields.acceptableValues'),
      dataIndex: 'acceptable',
      key: 'acceptable',
    },
    {
      label: t('bulkSchedule.fields.description'),
      dataIndex: 'description',
      key: 'description',
    },
  ];

  // Map: field key -> label & example
  const FIELD_INFO: Record<
    string,
    {
      label: string;
      example: string;
      description: string;
      configKey?: keyof InvitationConfig;
    }
  > = {
    Location: {
      label: t('bulkSchedule.fieldInfo.location.label'),
      example: t('bulkSchedule.fieldInfo.location.example'),
      description: t('bulkSchedule.fieldInfo.location.description'),
    },
    'First Name': {
      label: t('bulkSchedule.fieldInfo.firstName.label'),
      example: t('bulkSchedule.fieldInfo.firstName.example'),
      description: t('bulkSchedule.fieldInfo.firstName.description'),
      configKey: 'firstName',
    },
    'Last Name': {
      label: t('bulkSchedule.fieldInfo.lastName.label'),
      example: t('bulkSchedule.fieldInfo.lastName.example'),
      description: t('bulkSchedule.fieldInfo.lastName.description'),
      configKey: 'lastName',
    },
    Email: {
      label: t('bulkSchedule.fieldInfo.email.label'),
      example: t('bulkSchedule.fieldInfo.email.example'),
      description: t('bulkSchedule.fieldInfo.email.description'),
      configKey: 'email',
    },
    'Visit Phone': {
      label: t('bulkSchedule.fieldInfo.visitPhone.label'),
      example: t('bulkSchedule.fieldInfo.visitPhone.example'),
      description: t('bulkSchedule.fieldInfo.visitPhone.description'),
      configKey: 'visitPhone',
    },
    'Visitor Type': {
      label: t('bulkSchedule.fieldInfo.visitorType.label'),
      example: t('bulkSchedule.fieldInfo.visitorType.example'),
      description: t('bulkSchedule.fieldInfo.visitorType.description'),
    },
    'Start Time': {
      label: t('bulkSchedule.fieldInfo.startTime.label'),
      example: t('bulkSchedule.fieldInfo.startTime.example'),
      description: t('bulkSchedule.fieldInfo.startTime.description'),
    },
    'End Time': {
      label: t('bulkSchedule.fieldInfo.endTime.label'),
      example: t('bulkSchedule.fieldInfo.endTime.example'),
      description: t('bulkSchedule.fieldInfo.endTime.description'),
    },
    'Visitor Company': {
      label: t('bulkSchedule.fieldInfo.visitCompany.label'),
      example: t('bulkSchedule.fieldInfo.visitCompany.example'),
      description: t('bulkSchedule.fieldInfo.visitCompany.description'),
      configKey: 'visitCompany',
    },
    Host: {
      label: t('bulkSchedule.fieldInfo.host.label'),
      example: t('bulkSchedule.fieldInfo.host.example'),
      description: t('bulkSchedule.fieldInfo.host.description'),
      configKey: 'host',
    },
    'Visit Purpose': {
      label: t('bulkSchedule.fieldInfo.visitPurpose.label'),
      example: t('bulkSchedule.fieldInfo.visitPurpose.example'),
      description: t('bulkSchedule.fieldInfo.visitPurpose.description'),
      configKey: 'visitPurpose',
    },
    'Visit Note': {
      label: t('bulkSchedule.fieldInfo.visitNote.label'),
      example: t('bulkSchedule.fieldInfo.visitNote.example'),
      description: t('bulkSchedule.fieldInfo.visitNote.description'),
      configKey: 'visitNotes',
    },
    'Group First Name': {
      label: t('bulkSchedule.fieldInfo.groupFirstName.label'),
      example: t('bulkSchedule.fieldInfo.groupFirstName.example'),
      description: t('bulkSchedule.fieldInfo.groupFirstName.description'),
    },
    'Group Last Name': {
      label: t('bulkSchedule.fieldInfo.groupLastName.label'),
      example: t('bulkSchedule.fieldInfo.groupLastName.example'),
      description: t('bulkSchedule.fieldInfo.groupLastName.description'),
    },
  };

  const ALWAYS_INCLUDED = [
    'Location',
    'Visitor Type',
    'Start Time',
    'End Time',
  ];

  const parseFlexibleDate = (value: string): Dayjs | null => {
    if (!value) return null;

    const cleaned = value.trim().replace(/\u00A0/g, '');
    const formats = [
      'YYYY-MM-DD h:mm A',
      'YYYY-MM-DD hh:mm A',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DD h:mm',
      'YYYY-MM-DD HH:mm',
      'MM/DD/YY h:mm A',
      'MM/DD/YYYY h:mm A',
      'MM/DD/YY hh:mm A',
      'MM/DD/YYYY hh:mm A',
      'MM/DD/YYYY h:mm',
      'MM/DD/YYYY HH:mm',
      'M/D/YY H:mm',
      'M/D/YY h:mm A',
      'M/D/YYYY H:mm',
      'M/D/YYYY h:mm A',
      'MM/DD/YY H:mm',
      'MM/DD/YYYY H:mm',
    ];

    for (const fmt of formats) {
      const parsed = opTime(cleaned, fmt, true);
      if (parsed.isValid()) return parsed;
    }
    return null;
  };

  const getIncludedFields = (
    config: InvitationConfig | null | undefined,
  ): string[] => {
    if (!config) return ALWAYS_INCLUDED;

    return Object.entries(FIELD_INFO)
      .filter(
        ([key, info]) =>
          ALWAYS_INCLUDED.includes(key) ||
          (info.configKey &&
            (config[info.configKey] as ConfigItem)?.included === 1),
      )
      .map(([key]) => key);
  };

  const INCLUDED_HEADERS = getIncludedFields(invitationConfig);

  const data = Object.entries(FIELD_INFO).map(([field, info]) => {
    const isAlwaysRequired = ALWAYS_INCLUDED.includes(field);
    const isRequired =
      isAlwaysRequired ||
      (info.configKey &&
        invitationConfig &&
        (invitationConfig[info.configKey] as ConfigItem)?.required === 1);

    return {
      field: info.label,
      required: isRequired ? t('Yes') : t('No'),
      acceptable: `${t('E.g.')} ${info.example}`,
      description: info.description,
    };
  });

  const handleUpload = async () => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;

      // Parse the first line to get original headers (before Papa.parse processes them)
      const firstLineEnd = text.indexOf('\n');
      const headerLine =
        firstLineEnd > 0 ? text.substring(0, firstLineEnd).trim() : text.trim();

      // Parse headers manually to detect duplicates
      const rawHeaders: string[] = [];
      let inQuotes = false;
      let currentHeader = '';

      for (let i = 0; i < headerLine.length; i++) {
        const char = headerLine[i];

        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          rawHeaders.push(currentHeader.trim());
          currentHeader = '';
        } else {
          currentHeader += char;
        }
      }
      // Add the last header
      if (currentHeader) {
        rawHeaders.push(currentHeader.trim());
      }

      // Check for duplicate headers in raw headers
      const headerCounts = new Map<string, number>();
      rawHeaders.forEach((header) => {
        headerCounts.set(header, (headerCounts.get(header) || 0) + 1);
      });
      const duplicateHeaders = Array.from(headerCounts.entries())
        .filter(([, count]) => count > 1)
        .map(([header]) => header);

      const parsed = Papa.parse<BulkScheduleCsvRow>(text, {
        header: true,
        // @ts-expect-error - transformHeader is supported but not in type definitions
        transformHeader: (header: string) => header.trim(),
      });
      const actualHeaders = parsed.meta.fields || [];

      const missingHeaders = INCLUDED_HEADERS.filter(
        (h) => !actualHeaders.includes(h),
      );

      if (duplicateHeaders.length > 0 || missingHeaders.length > 0) {
        const statusLines: JSX.Element[] = [];

        statusLines.push(
          <div key="invalid-format">
            <strong>{t('bulkSchedule.uploadErrors.invalidFormat')}</strong>
          </div>,
        );

        if (duplicateHeaders.length > 0) {
          statusLines.push(
            <div key="duplicate-headers" style={{ color: 'red', marginTop: 8 }}>
              {t('bulkSchedule.uploadErrors.duplicateHeaders', {
                headers: duplicateHeaders.join(', '),
              })}
            </div>,
          );
        }

        if (missingHeaders.length > 0) {
          statusLines.push(
            <div key="missing-headers" style={{ color: 'red', marginTop: 8 }}>
              {t('bulkSchedule.uploadErrors.missingHeaders', {
                headers: missingHeaders.join(', '),
              })}
            </div>,
          );
        }
        setUploadStatus(statusLines);
        return;
      }

      const {
        errors,
        validEntries: validatedEntries,
        totalEntries,
      } = validateCsv(
        Papa.unparse(parsed.data),
        invitationConfig!,
        fileList[0].name,
      );
      setValidEntries(validatedEntries);

      const statusLines: JSX.Element[] = [];
      if (errors.length > 0) {
        statusLines.push(
          <div key="upload-summary">
            {t('bulkSchedule.uploadErrors.successfulUpload', {
              valid: validatedEntries.length,
              total: totalEntries,
            })}
          </div>,
        );
        statusLines.push(<br key="br-1" />);
        statusLines.push(
          <strong key="errors-title">
            {t('bulkSchedule.uploadErrors.errorsTitle')}
          </strong>,
        );

        errors.forEach((err, i) => {
          statusLines.push(
            <div key={`error-${i}`} style={{ margin: '8px 0' }}>
              {err.split('\n').map((line, idx) =>
                idx === 0 ? (
                  <div key={idx}>{line}</div>
                ) : (
                  <div key={idx} style={{ color: 'red' }}>
                    {line.trim()}
                  </div>
                ),
              )}
            </div>,
          );
        });

        statusLines.push(
          <div key="import-instruction" style={{ marginTop: 12 }}>
            {t('bulkSchedule.uploadErrors.importInstruction', {
              count: validatedEntries.length,
            })}
          </div>,
        );
      } else {
        statusLines.push(
          <div key="upload-success">
            {t('bulkSchedule.uploadErrors.successfulUpload', {
              valid: validatedEntries.length,
              total: totalEntries,
            })}
          </div>,
        );
      }

      setUploadStatus(statusLines);
    };
    const fileObject = fileList[0]?.originFileObj;
    if (!fileObject) {
      return;
    }

    reader.readAsText(fileObject);
    notificationService.show(
      'success',
      t('bulkSchedule.uploadErrors.scheduleSuccess'),
    );
  };

  const validateCsv = (
    csvText: string,
    config: InvitationConfig,
    fileName: string,
  ) => {
    const results = Papa.parse<BulkScheduleCsvRow>(csvText, {
      header: true,
      // @ts-expect-error - transformHeader is supported but not in type definitions
      transformHeader: (header: string) => header.trim(),
    });
    const errors: string[] = [];
    const validItems: BulkScheduleCsvRow[] = [];

    const hostNames = hosts.map((host) =>
      formatFullName(
        host.identity.firstName || '',
        host.identity.middleName || '',
        host.identity.lastName || '',
      ),
    );

    const isRequired = (field: keyof InvitationConfig) => {
      const fieldConfig = config[field];
      if (
        typeof fieldConfig === 'object' &&
        fieldConfig !== null &&
        'included' in fieldConfig &&
        'required' in fieldConfig
      ) {
        return fieldConfig.included === 1 && fieldConfig.required === 1;
      }
      return false;
    };

    const rows = results.data.filter((row) =>
      Object.values(row).some((cell) => {
        return typeof cell === 'string' && cell.trim() !== '';
      }),
    );

    // Track failed primary visitors by their row index
    const failedPrimaryVisitorIndices = new Set<number>();
    const isEmpty = (value: string | null | undefined) =>
      !value || value.trim() === '';

    // First pass: validate all rows and track failed primary visitors
    const rowValidationResults = rows.map((row, rowIndex) => {
      const rowErrors: string[] = [];

      const fullName =
        formatFullName(row['First Name'] || '', '', row['Last Name'] || '') ||
        '(Unknown)';

      // Check if this is a group member (has Group First Name and Group Last Name)
      const isGroupMember =
        !isEmpty(row['Group First Name']) && !isEmpty(row['Group Last Name']);

      // For group members, skip visit-level validations (Location, Type, Time, Host, etc.)
      // Only validate personal info (First Name, Last Name, Email, Phone)
      // Group members completely inherit visit details from their parent visitor
      if (!isGroupMember) {
        // Validate Location
        if (isEmpty(row.Location)) {
          rowErrors.push(t('bulkSchedule.rowErrors.missingLocation'));
        } else if (
          !locations
            .map((loc) => loc.name.toLowerCase())
            .includes(row.Location.trim().toLowerCase())
        ) {
          rowErrors.push(
            t('bulkSchedule.rowErrors.invalidLocation', {
              location: row.Location,
            }),
          );
        }

        // Validate Visitor Type
        if (isEmpty(row['Visitor Type'])) {
          rowErrors.push(t('bulkSchedule.rowErrors.missingVisitorType'));
        } else if (
          !visitorTypes
            .map((vt) => vt.name?.toLowerCase())
            .includes(row['Visitor Type'].trim().toLowerCase())
        ) {
          rowErrors.push(
            t('bulkSchedule.rowErrors.invalidVisitorType', {
              visitorType: row['Visitor Type'],
            }),
          );
        }

        // Validate Start/End Time
        const startTimeStr = row['Start Time']?.trim();
        const endTimeStr = row['End Time']?.trim();

        const startTimeParsed = parseFlexibleDate(startTimeStr);
        const endTimeParsed = parseFlexibleDate(endTimeStr);

        // Find the location's timezone
        const rowLocation = locations.find(
          (loc) => loc.name.toLowerCase() === row.Location.trim().toLowerCase(),
        );
        const locationTimezone = rowLocation?.timezone;

        // Interpret the parsed times as being in the location's timezone
        // (CSV times are assumed to be in the visit location's local time)
        const startTime =
          startTimeParsed && locationTimezone
            ? opTime.tz(
                startTimeParsed.format('YYYY-MM-DD HH:mm:ss'),
                locationTimezone,
              )
            : startTimeParsed;

        const endTime =
          endTimeParsed && locationTimezone
            ? opTime.tz(
                endTimeParsed.format('YYYY-MM-DD HH:mm:ss'),
                locationTimezone,
              )
            : endTimeParsed;

        // Get current time in the location's timezone (or browser timezone as fallback)
        const nowInLocationTz = locationTimezone
          ? opTime().tz(locationTimezone)
          : opTime();

        if (!startTime) {
          rowErrors.push(t('bulkSchedule.rowErrors.invalidStartTime'));
        } else if (startTime.isBefore(nowInLocationTz)) {
          rowErrors.push(t('bulkSchedule.rowErrors.startTimePast'));
        }

        if (!endTime) {
          rowErrors.push(t('bulkSchedule.rowErrors.invalidEndTime'));
        } else if (startTime && !startTime.isBefore(endTime)) {
          rowErrors.push(t('bulkSchedule.rowErrors.startTimeBeforeEndTime'));
        }

        // Validate Host
        if (isRequired('host') && isEmpty(row.Host)) {
          rowErrors.push(t('bulkSchedule.rowErrors.missingHost'));
        }
        if (
          row.Host &&
          !hostNames
            .map((h) => h.toLowerCase())
            .includes(row.Host.trim().toLowerCase())
        ) {
          rowErrors.push(
            t('bulkSchedule.rowErrors.invalidHost', { host: row.Host }),
          );
        }

        // Validate Company, Purpose, Note
        if (isRequired('visitCompany') && isEmpty(row['Visitor Company'])) {
          rowErrors.push(t('bulkSchedule.rowErrors.missingCompany'));
        }
        if (isRequired('visitPurpose') && isEmpty(row['Visit Purpose'])) {
          rowErrors.push(t('bulkSchedule.rowErrors.missingPurpose'));
        }
        if (isRequired('visitNotes') && isEmpty(row['Visit Note'])) {
          rowErrors.push(t('bulkSchedule.rowErrors.missingNote'));
        }
      }

      // Always validate personal info (First Name, Last Name, Email, Phone)
      if (isEmpty(row['First Name'])) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingFirstName'));
      }
      if (isEmpty(row['Last Name'])) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingLastName'));
      }
      if (isRequired('visitPhone') && isEmpty(row['Visit Phone'])) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingVisitPhone'));
      }

      if (isRequired('email') && isEmpty(row.Email)) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingEmail'));
      }
      if (row.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.Email.trim())) {
        rowErrors.push(t('bulkSchedule.rowErrors.invalidEmail'));
      }

      // Validate group member reference if applicable
      if (isGroupMember) {
        const groupFirstName = row['Group First Name']?.trim() || '';
        const groupLastName = row['Group Last Name']?.trim() || '';
        const primaryVisitorKey = `${groupFirstName.toLowerCase()}|${groupLastName.toLowerCase()}`;

        // Check if the referenced primary visitor exists in the CSV and appears before this group member
        // Use rowIndex directly instead of indexOf to handle duplicate rows correctly
        const primaryVisitorIndex = rows.findIndex((r, rIdx) => {
          const firstName = (r['First Name']?.trim() || '').toLowerCase();
          const lastName = (r['Last Name']?.trim() || '').toLowerCase();
          const key = `${firstName}|${lastName}`;
          const isNotGroupMember =
            isEmpty(r['Group First Name']) || isEmpty(r['Group Last Name']);
          return (
            key === primaryVisitorKey && isNotGroupMember && rIdx < rowIndex
          );
        });

        if (primaryVisitorIndex === -1) {
          // Check if primary visitor exists but is positioned after this group member
          const primaryExistsAfter = rows.some((r, rIdx) => {
            const firstName = (r['First Name']?.trim() || '').toLowerCase();
            const lastName = (r['Last Name']?.trim() || '').toLowerCase();
            const key = `${firstName}|${lastName}`;
            const isNotGroupMember =
              isEmpty(r['Group First Name']) || isEmpty(r['Group Last Name']);
            return (
              key === primaryVisitorKey && isNotGroupMember && rIdx > rowIndex
            );
          });

          if (primaryExistsAfter) {
            rowErrors.push(
              t('bulkSchedule.rowErrors.primaryVisitorMustPrecedeGroupMember', {
                primaryName: `${groupFirstName} ${groupLastName}`,
                defaultValue:
                  'Primary visitor "{{primaryName}}" must appear before this group member in the CSV',
              }),
            );
          } else {
            rowErrors.push(
              t('bulkSchedule.rowErrors.primaryVisitorNotFound', {
                primaryName: `${groupFirstName} ${groupLastName}`,
              }),
            );
          }
        }
      }

      // Track failed primary visitors by index for second pass
      if (!isGroupMember && rowErrors.length > 0) {
        failedPrimaryVisitorIndices.add(rowIndex);
      }

      return { row, rowErrors, fullName, isGroupMember, rowIndex };
    });

    // Second pass: mark group members as failed if their primary visitor failed
    rowValidationResults.forEach(
      ({ row, rowErrors, fullName, isGroupMember, rowIndex }) => {
        if (isGroupMember) {
          const groupFirstName = row['Group First Name']?.trim() || '';
          const groupLastName = row['Group Last Name']?.trim() || '';
          const primaryVisitorKey = `${groupFirstName.toLowerCase()}|${groupLastName.toLowerCase()}`;

          // Find the closest preceding primary visitor (same logic as import)
          const closestPrimaryIndex = rows
            .slice(0, rowIndex)
            .map((r, idx) => ({ row: r, originalIndex: idx }))
            .reverse()
            .find((item) => {
              const firstName = (
                item.row['First Name']?.trim() || ''
              ).toLowerCase();
              const lastName = (
                item.row['Last Name']?.trim() || ''
              ).toLowerCase();
              const key = `${firstName}|${lastName}`;
              const isNotGroupMember =
                isEmpty(item.row['Group First Name']) ||
                isEmpty(item.row['Group Last Name']);
              return key === primaryVisitorKey && isNotGroupMember;
            })?.originalIndex;

          // Check if the matched primary visitor failed validation
          if (
            closestPrimaryIndex !== undefined &&
            failedPrimaryVisitorIndices.has(closestPrimaryIndex)
          ) {
            rowErrors.push(
              t('bulkSchedule.rowErrors.primaryVisitorFailed', {
                primaryName: `${groupFirstName} ${groupLastName}`,
              }),
            );
          }
        }

        if (rowErrors.length > 0) {
          errors.push(
            t('bulkSchedule.errorSummary', { fullName, fileName }) +
              '\n' +
              `  ${rowErrors.join(', ')}`,
          );
        } else {
          validItems.push(row);
        }
      },
    );

    return { errors, validEntries: validItems, totalEntries: rows.length };
  };

  const handleImport = async () => {
    try {
      const isEmpty = (value: string | null | undefined) =>
        !value || value.trim() === '';

      // Separate primary visitors with their original indices
      const primaryVisitorsWithIndices: Array<{
        row: BulkScheduleCsvRow;
        originalIndex: number;
      }> = [];
      validEntries.forEach((row, idx) => {
        if (
          isEmpty(row['Group First Name']) ||
          isEmpty(row['Group Last Name'])
        ) {
          primaryVisitorsWithIndices.push({ row, originalIndex: idx });
        }
      });

      // Build a map to find group members for each primary visitor (name-based matching)
      // Group members are matched to primary visitors by Group First Name + Group Last Name
      // For duplicate names, match to the closest preceding primary visitor
      const primaryVisitorIndexToGroupMembers = new Map<
        number,
        BulkScheduleCsvRow[]
      >();
      const primaryVisitorNameToIndices = new Map<string, number[]>();

      // First pass: Map primary visitor names to their indices (support duplicates)
      validEntries.forEach((row, idx) => {
        const isGroupMember =
          !isEmpty(row['Group First Name']) && !isEmpty(row['Group Last Name']);

        if (!isGroupMember) {
          // This is a primary visitor
          const firstName = (row['First Name']?.trim() || '').toLowerCase();
          const lastName = (row['Last Name']?.trim() || '').toLowerCase();
          const key = `${firstName}|${lastName}`;

          if (!primaryVisitorNameToIndices.has(key)) {
            primaryVisitorNameToIndices.set(key, []);
          }
          primaryVisitorNameToIndices.get(key)!.push(idx);
          primaryVisitorIndexToGroupMembers.set(idx, []);
        }
      });

      // Second pass: Match group members to primary visitors by name
      // For duplicates, choose the closest preceding primary visitor
      validEntries.forEach((row, idx) => {
        const isGroupMember =
          !isEmpty(row['Group First Name']) && !isEmpty(row['Group Last Name']);

        if (isGroupMember) {
          const groupFirstName = (
            row['Group First Name']?.trim() || ''
          ).toLowerCase();
          const groupLastName = (
            row['Group Last Name']?.trim() || ''
          ).toLowerCase();
          const primaryKey = `${groupFirstName}|${groupLastName}`;

          const indices = primaryVisitorNameToIndices.get(primaryKey);
          if (indices && indices.length > 0) {
            // Find the closest preceding primary visitor
            const validIndices = indices.filter((i) => i < idx);
            if (validIndices.length > 0) {
              const primaryIdx = validIndices[validIndices.length - 1];
              primaryVisitorIndexToGroupMembers.get(primaryIdx)!.push(row);
            }
          }
        }
      });

      // Create invitation payloads for primary visitors (including their group members)
      const invitationPayloads = primaryVisitorsWithIndices.map(
        ({ row, originalIndex }) => {
          const location = locations.find(
            (loc) =>
              loc.name.toLowerCase() ===
              (row.Location ?? '').trim().toLowerCase(),
          );
          const tz = location?.timezone;

          const start = toUTCByTimezone(
            parseFlexibleDate(row['Start Time'] ?? ''),
            tz,
          )?.format(DATE_TIME_FORMAT);
          const end = toUTCByTimezone(
            parseFlexibleDate(row['End Time'] ?? ''),
            tz,
          )?.format(DATE_TIME_FORMAT);

          const host = hosts.find(
            (h) =>
              formatFullName(
                h.identity.firstName || '',
                h.identity.middleName || '',
                h.identity.lastName || '',
              )
                .toLowerCase()
                .trim() === (row.Host ?? '').trim().toLowerCase(),
          );

          const visitorType = visitorTypes.find(
            (vt) =>
              vt.name?.toLowerCase() ===
              (row['Visitor Type'] ?? '').trim().toLowerCase(),
          );

          return {
            row, // Keep reference to original row for name matching
            payload: {
              locationId: location?.id,
              visitorTypeId: visitorType?.id,
              company:
                invitationConfig?.visitCompany.included === 1
                  ? row['Visitor Company']?.trim() || null
                  : null,
              scheduleIn: start,
              scheduleOut: end,
              hostId: invitationConfig?.host.included === 1 ? host?.id : null,
              purpose:
                invitationConfig?.visitPurpose.included === 1
                  ? row['Visit Purpose']?.trim() || null
                  : null,
              note:
                invitationConfig?.visitNotes.included === 1
                  ? row['Visit Note']?.trim() || null
                  : null,
              visitors: [
                // Primary visitor
                {
                  firstName:
                    invitationConfig?.firstName.included === 1
                      ? row['First Name']?.trim() || null
                      : null,
                  middleName: null,
                  lastName:
                    invitationConfig?.lastName.included === 1
                      ? row['Last Name']?.trim() || null
                      : null,
                  email:
                    invitationConfig?.email.included === 1
                      ? row.Email?.trim() || null
                      : null,
                  mobilePhone:
                    invitationConfig?.visitPhone.included === 1
                      ? row['Visit Phone']?.trim() || null
                      : null,
                  userId: null,
                  status: STATUS.PENDING.id,
                  signIn: null,
                  signOut: null,
                },
                // Group members (matched by Group First Name + Group Last Name)
                ...(
                  primaryVisitorIndexToGroupMembers.get(originalIndex) || []
                ).map((member: BulkScheduleCsvRow) => ({
                  firstName:
                    invitationConfig?.firstName.included === 1
                      ? member['First Name']?.trim() || null
                      : null,
                  middleName: null,
                  lastName:
                    invitationConfig?.lastName.included === 1
                      ? member['Last Name']?.trim() || null
                      : null,
                  email:
                    invitationConfig?.email.included === 1
                      ? member.Email?.trim() || null
                      : null,
                  mobilePhone:
                    invitationConfig?.visitPhone.included === 1
                      ? member['Visit Phone']?.trim() || null
                      : null,
                  userId: null,
                  status: STATUS.PENDING.id,
                  signIn: null,
                  signOut: null,
                })),
              ],
            },
          };
        },
      );

      // Create primary visits and get visit IDs
      const createdVisits = await Promise.all(
        invitationPayloads.map(({ payload }) =>
          heliumSend('newInvitation', [orgId], payload),
        ),
      );

      // Map primary visitor row indices to visit IDs
      const primaryVisitorIndexToVisitId = new Map<number, number>();
      const failedPrimaryVisitors: string[] = [];

      primaryVisitorsWithIndices.forEach(({ row, originalIndex }, index) => {
        const firstName = row['First Name']?.trim() || '';
        const lastName = row['Last Name']?.trim() || '';
        const fullName = `${firstName} ${lastName}`;
        const response = createdVisits[index] as any;
        const visitId = response?.data?.[0]?.id;

        if (visitId) {
          primaryVisitorIndexToVisitId.set(originalIndex, visitId);
        } else {
          failedPrimaryVisitors.push(fullName);
        }
      });

      // Check for unmatched group members (defensive check - should be caught by validation)
      const unmatchedGroupMembers: string[] = [];
      validEntries.forEach((row, idx) => {
        const isGroupMember =
          !isEmpty(row['Group First Name']) && !isEmpty(row['Group Last Name']);

        if (isGroupMember) {
          const groupFirstName = (
            row['Group First Name']?.trim() || ''
          ).toLowerCase();
          const groupLastName = (
            row['Group Last Name']?.trim() || ''
          ).toLowerCase();
          const primaryKey = `${groupFirstName}|${groupLastName}`;

          const indices = primaryVisitorNameToIndices.get(primaryKey);
          // Check if there's a valid primary visitor (exists AND precedes this group member)
          const hasValidPrimary = indices && indices.some((i) => i < idx);

          if (!hasValidPrimary) {
            const memberName = `${row['First Name']?.trim() || ''} ${row['Last Name']?.trim() || ''}`;
            const reason = indices
              ? 'precedes member' // Primary exists but is after this member
              : 'not found'; // Primary doesn't exist in validEntries
            unmatchedGroupMembers.push(
              `${memberName} → ${row['Group First Name']?.trim() || ''} ${row['Group Last Name']?.trim() || ''} (${reason})`,
            );
          }
        }
      });

      // Update upload status with import results (excluding unmatchedGroupMembers)
      const statusLines: JSX.Element[] = uploadStatus ? [...uploadStatus] : [];

      // Add unmatched group members warning to upload status (not notification)
      if (unmatchedGroupMembers.length > 0) {
        statusLines.push(<br key="br-unmatched" />);
        statusLines.push(
          <div key="unmatched" style={{ color: 'orange', marginTop: 12 }}>
            <strong>
              {t('bulkSchedule.warnings.unmatchedGroupMembersTitle', {
                count: unmatchedGroupMembers.length,
              })}
            </strong>
            <div style={{ marginTop: 4 }}>
              {unmatchedGroupMembers.map((detail, idx) => (
                <div key={idx}>• {detail}</div>
              ))}
            </div>
          </div>,
        );
        setUploadStatus(statusLines);
      }

      // Show critical warnings/errors in notifications
      const warnings: string[] = [];
      if (failedPrimaryVisitors.length > 0) {
        warnings.push(
          t('bulkSchedule.warnings.failedPrimaryVisitors', {
            count: failedPrimaryVisitors.length,
            names: failedPrimaryVisitors.join(', '),
          }),
        );
      }

      if (warnings.length > 0) {
        notificationService.show('warning', warnings.join('\n\n'));
      } else if (unmatchedGroupMembers.length === 0) {
        notificationService.show(
          'success',
          t('bulkSchedule.uploadErrors.scheduleSuccess'),
        );
      }

      dispatch(
        setVisitsStartDate(opTime().startOf('day').format(DATE_TIME_FORMAT)),
      );
      dispatch(
        setVisitsEndDate(opTime().endOf('day').format(DATE_TIME_FORMAT)),
      );
      dispatch(fetchVisits({ orgId }));

      navigate(`/o/${orgId}/visitors`);
    } catch {
      notificationService.show(
        'error',
        t('bulkSchedule.uploadErrors.scheduleError'),
      );
    }
  };

  return (
    <OpPage title={t('bulkSchedule.title')} tooltip={t('bulkSchedule.tooltip')}>
      <OpTypography.Paragraph>
        {t('bulkSchedule.description1')}
      </OpTypography.Paragraph>
      <OpTypography.Paragraph>
        {t('bulkSchedule.description2')}
      </OpTypography.Paragraph>

      <OpCollapse
        defaultActiveKey={['1']}
        items={[
          {
            key: '1',
            label: t('bulkSchedule.validFields'),
            children: (
              <OpTable
                uiStateKey={
                  tableStateKeys.UsersImportPageValidFieldsLegacyTable
                }
                columns={columns}
                dataSource={data}
                pagination={false}
                rowKey="field"
                scroll={{ x: 'max-content' }}
                allowGlobalSearch={false}
                allowShowHideColumns={false}
                allowExport={false}
              />
            ),
          },
        ]}
      />

      {hasVisitorsBulkWrite ? (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <OpUpload
              beforeUpload={() => false}
              onChange={({ fileList: nextFileList }) =>
                setFileList(nextFileList)
              }
              showUploadList={false}
              fileList={fileList}
              accept=".csv"
              maxCount={1}
            >
              <OpButton icon={<UploadOutlined />}>
                {t('bulkSchedule.selectCsv')}
              </OpButton>
            </OpUpload>

            {fileList.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'var(--colorInfoBgHover)',
                  padding: '0px 8px',
                  borderRadius: 4,
                }}
              >
                <span>{fileList[0].name}</span>
                <OpButton
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    setFileList([]);
                    setUploadStatus(null);
                  }}
                  style={{ marginLeft: 8 }}
                />
              </div>
            )}
          </div>

          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
            <OpButton
              type="primary"
              onClick={() => {
                confirmModal({
                  title: t('bulkSchedule.uploadConfirmationTitle'),
                  content: t('bulkSchedule.uploadConfirmationContent'),
                  okText: t('bulkSchedule.ok'),
                  cancelText: t('bulkSchedule.cancel'),
                  onOk: handleUpload,
                });
              }}
              disabled={!fileList.length}
            >
              {t('bulkSchedule.selectCsv')}
            </OpButton>

            <OpButton
              icon={<DownloadOutlined />}
              href={sampleCsvUrl}
              download="bulk-schedule-sample.csv"
            >
              {t('bulkSchedule.downloadSampleCsv')}
            </OpButton>
          </div>

          <div style={{ marginTop: 24 }}>
            <Title level={4}>{t('bulkSchedule.uploadStatusTitle')}</Title>
            <div
              style={{
                minHeight: 200,
                background: 'var(--colorInfoBg)',
                padding: 16,
                border: '1px solid var(--colorBorder)',
                borderRadius: 8,
                whiteSpace: 'pre-wrap',
                height: 300,
                overflowY: 'auto',
              }}
            >
              {uploadStatus || t('bulkSchedule.noFileUploaded')}
            </div>
          </div>

          <div style={{ marginTop: 24 }}>
            <OpButton
              type="primary"
              onClick={() => {
                confirmModal({
                  title: t('bulkSchedule.importConfirmationTitle'),
                  content: t('bulkSchedule.importConfirmationContent'),
                  okText: t('bulkSchedule.import'),
                  cancelText: t('bulkSchedule.cancel'),
                  onOk: handleImport,
                });
              }}
              disabled={!uploadStatus}
            >
              {t('bulkSchedule.import')}
            </OpButton>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
          <OpButton
            icon={<DownloadOutlined />}
            href={sampleCsvUrl}
            download="bulk-schedule-sample.csv"
          >
            {t('bulkSchedule.downloadSampleCsv')}
          </OpButton>
        </div>
      )}
    </OpPage>
  );
};

export default BulkSchedule;
